import {Store, useStore} from "@tanstack/react-store";

export interface ExtensionState {
  /** When true, the extension sidebar is visible. */
  isOpen: boolean;
  /** Width of the extension sidebar in pixels. */
  width: number;
  /** The ID of the currently active extension. */
  activeExtension: string;
  // Actions
  toggleSidebar: () => void;
  setWidth: (width: number) => void;
  setActiveExtension: (extensionId: string) => void;
  focusApp: () => void;
  focusAssistant: () => void;
}

export const MIN_SIDEBAR_WIDTH = 320;
export const DEFAULT_SIDEBAR_WIDTH = 400;
export const AI_ASSISTANT_EXTENSION_ID = "ai-assistant";

function activateExtension(extensionId: string) {
  extensionStore.setState((state) => ({
    ...state,
    activeExtension: extensionId,
    // Automatically open the sidebar if we switch to an extension while closed.
    isOpen: true,
  }));
}

export const extensionStore = new Store<ExtensionState>({
  isOpen: false,
  width: DEFAULT_SIDEBAR_WIDTH,
  activeExtension: AI_ASSISTANT_EXTENSION_ID,

  toggleSidebar: () => {
    extensionStore.setState((state) => ({
      ...state,
      isOpen: !state.isOpen,
    }));
  },

  setWidth: (width: number) => {
    extensionStore.setState((state) => {
      // Dynamic max width: 50% of current viewport width, or fallback to a sensible 800px limit
      // if window is not defined (e.g. during SSR)
      const dynamicMaxWidth = typeof window !== "undefined" ? window.innerWidth * 0.5 : 800;

      return {
        ...state,
        // Clamp between min and max
        width: Math.min(Math.max(width, MIN_SIDEBAR_WIDTH), dynamicMaxWidth),
      };
    });
  },

  setActiveExtension: activateExtension,

  focusApp: () => {
    extensionStore.setState((state) => ({
      ...state,
      isOpen: false,
    }));
  },

  focusAssistant: () => {
    activateExtension(AI_ASSISTANT_EXTENSION_ID);
  },
});

export function useExtensionStore<T>(selector: (state: ExtensionState) => T): T {
  return useStore(extensionStore, selector);
}
